/**
 * Plans — pricing, checkout, plan changes and billing management.
 *
 * Floodlight (v1.71): the original black ground with its blue glow, glass
 * cards and the purple/blue accents, set in Floodlight type (Archivo caps,
 * spaced kickers, tabular prices). The plan under the pointer lifts with a
 * purple bloom and its accent bar draws in; buttons and rows brighten
 * (components/floodlight/floodlightCss.js). All motion is off under reduced
 * motion.
 *
 * Money paths (functions/billing/index.js):
 *   new subscriber      → createCheckoutSession → Stripe Checkout → back here
 *                         (?checkout=success&session_id=…) → confirmCheckoutSession
 *   existing subscriber → previewPlanChange → confirm → changeSubscriptionPlan
 *   manage / cancel     → createBillingPortalSession, resumeSubscription
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { View, Text, Pressable, StyleSheet, Platform, useWindowDimensions, ActivityIndicator, Modal } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '../context/NavigationContext';
import CheckoutTerms from '../components/CheckoutTerms';
import { useAuth } from '../context/AuthContext';
import Appear from '../components/motion/Appear';
import AnimatedNumber from '../components/motion/AnimatedNumber';
import Collapse from '../components/motion/Collapse';
import Reveal from '../components/motion/Reveal';
import { IS_WEB } from '../utils/motion';
import injectCss from '../utils/injectCss';
import { showToast } from '../utils/toast';
import {
    subscribeToPlanCatalog, startCheckout, confirmCheckout, previewPlanChange, changePlan,
    openBillingPortal, resumeSubscription,
} from '../services/stripeService';
import { planList, displayFeatures, planAction, effectivePlan, hasStripeSubscription, formatPrice, yearlySavings, limitLabel } from '../utils/billingPlans';
import { FLPage, FLButton, FLHeadline, FLMarquee, FLKicker, FLRule, FL, FONT, display, label, body, nums, gutter, glass, ACCENT_GRADIENT } from '../components/floodlight';

const INCLUDES = [
    ['Fair odds on every price', 'Devigged against the full market, not one book.'],
    ['Every book side by side', 'The best number for each line, with how many books agree.'],
    ['Same-game parlays, optimized', 'Legs ranked by combined expected value, priced where you can bet them.'],
];

// What the bands spell out (the same facts as the includes below).
const BAND = ['Fair odds', 'Expected value', 'Best book', 'Same-game parlays'];

const FAQ = [
    { q: 'What counts as a search?', a: 'One search is one question you ask, like "nba player points" or "chiefs vs bills". Only searches that return results count against your limit.' },
    { q: 'When does my limit reset?', a: 'Monthly limits reset on the 1st of each month (UTC). Daily limits, if a plan has one, reset at midnight UTC.' },
    { q: 'Can I switch plans later?', a: 'Yes. Upgrades and downgrades apply right away and are prorated: you pay or are credited the difference for the rest of the billing period.' },
    { q: 'How do I cancel?', a: 'Open Manage billing on this page. Your plan stays active until the end of the period you already paid for, and you can resume any time before then.' },
    { q: 'Is my payment secure?', a: 'Checkout and billing run on Stripe. BetOptimized never sees or stores your card number.' },
];


// Plans-only: the lifted card rises above its neighbours; the recommended
// card carries a stronger purple bloom.
const PLANS_CSS = `
[data-plan-cell]:focus-within { z-index: 2; }
@media (hover: hover) and (pointer: fine) {
  [data-plan-cell]:hover { z-index: 2; }
  [data-fl-card][data-plan-cell="featured"]:hover { box-shadow: 0 18px 50px rgba(0,0,0,0.5), 0 0 0 1px rgba(162,89,255,0.45), 0 0 80px rgba(124,58,237,0.28); }
}
`;

function useColumns(width) {
    if (width >= 1180) return 4;
    if (width >= 700) return 2;
    return 1;
}

/** Largest display size at which every line fits the column (Floodlight caps at width 104 run ~0.68em a letter). */
const fitSize = (lines, accent, col, max) => Math.max(28, Math.min(max, Math.floor(Math.min(
    ...lines.map((l) => col / (l.length * 0.68 + 0.2)),
))));

export default function PlansScreen() {
    if (IS_WEB) injectCss('plans-fl', PLANS_CSS);
    const { navigateTo } = useNavigation();
    const { user, userProfile } = useAuth();
    const { width } = useWindowDimensions();
    const cols = useColumns(width);
    const pad = gutter(width);
    const wide = width >= 960;
    const contentW = Math.min(width, 1480) - pad * 2;

    const [catalog, setCatalog] = useState(null);
    const [billingInterval, setBillingInterval] = useState('month');
    const [busyKey, setBusyKey] = useState(null);
    const [banner, setBanner] = useState(null); // { tone, title, body }
    const [change, setChange] = useState(null); // { plan, interval, preview, loading, error }
    const [openFaq, setOpenFaq] = useState(null);
    const isAdmin = String(userProfile?.userType || '').toLowerCase() === 'admin';
    const isTester = isAdmin || userProfile?.billingTester === true;
    const [testMode, setTestMode] = useState(false);
    const mode = testMode && isTester ? 'test' : 'live';

    useEffect(() => subscribeToPlanCatalog((c) => setCatalog(c)), []);

    // Return from Stripe Checkout.
    useEffect(() => {
        if (Platform.OS !== 'web' || typeof window === 'undefined') return;
        const params = new URLSearchParams(window.location.search);
        const status = params.get('checkout') || (params.get('subscribed') === '1' ? 'success' : null);
        if (!status) return;
        const sessionId = params.get('session_id');
        const clean = () => { try { window.history.replaceState(null, '', window.location.pathname); } catch { /* ignore */ } };
        if (status === 'canceled') {
            setBanner({ tone: 'info', title: 'Checkout canceled', body: 'Nothing was charged. Pick a plan whenever you are ready.' });
            clean();
            return;
        }
        if (!user) return; // wait for auth, then confirm
        setBanner({ tone: 'pending', title: 'Confirming your subscription…', body: 'This takes a second.' });
        (async () => {
            try {
                const out = sessionId ? await confirmCheckout(sessionId) : null;
                const planName = out?.planType ? out.planType.charAt(0) + out.planType.slice(1).toLowerCase() : null;
                setBanner({ tone: 'success', title: planName ? `Welcome to ${planName}.` : 'You are subscribed.', body: 'Your new limits are live now. A receipt is on its way from Stripe.' });
            } catch (e) {
                setBanner({ tone: 'warn', title: 'Payment received, still syncing', body: 'Your plan will update within a minute. If it does not, contact support with your Stripe receipt.' });
            } finally {
                clean();
            }
        })();
    }, [user]);

    const plans = useMemo(() => planList(catalog), [catalog]);
    const current = effectivePlan(userProfile || {});
    const sub = userProfile?.subscription || {};
    const subscriber = hasStripeSubscription(userProfile);
    const maxSave = useMemo(() => Math.max(0, ...plans.filter((p) => p.key !== 'FREE').map((p) => yearlySavings(p).pct)), [plans]);

    const onAction = useCallback(async (plan, action) => {
        if (action.kind === 'signin') { navigateTo('Login', { redirectAfterLogin: 'Plans' }); return; }
        if (action.kind === 'current' && !user) { navigateTo('Register'); return; }
        if (action.kind === 'manage-resume') {
            setBusyKey(plan.key);
            try { await resumeSubscription(); showToast('Plan resumed. It will renew as normal.', 'success'); }
            catch (e) { showToast(e.message, 'error'); }
            finally { setBusyKey(null); }
            return;
        }
        if (action.kind === 'change') {
            setChange({ plan, interval: billingInterval, loading: true });
            try {
                const preview = await previewPlanChange(plan.key, billingInterval);
                setChange({ plan, interval: billingInterval, preview, loading: false });
            } catch (e) {
                setChange({ plan, interval: billingInterval, loading: false, error: e.message });
            }
            return;
        }
        if (action.kind === 'checkout') {
            setBusyKey(plan.key);
            try { await startCheckout({ planKey: plan.key, interval: billingInterval, mode }); }
            catch (e) { showToast(e.message, 'error'); setBusyKey(null); }
        }
    }, [billingInterval, mode, navigateTo, user]);

    const confirmChange = async () => {
        if (!change?.plan) return;
        setChange((c) => ({ ...c, applying: true }));
        try {
            await changePlan(change.plan.key, change.interval, change.preview?.prorationDate);
            setChange(null);
            showToast(`You're on ${change.plan.name} now.`, 'success');
        } catch (e) {
            setChange((c) => ({ ...c, applying: false, error: e.message }));
        }
    };

    const manage = async () => {
        setBusyKey('portal');
        try { await openBillingPortal(); } catch (e) { showToast(e.message, 'error'); setBusyKey(null); }
    };

    const cardWidth = cols === 1 ? '100%' : `${100 / cols}%`;
    const heroWide = contentW >= 760;
    const heroLines = heroWide ? ['Pay for the edge,', 'not the noise.'] : ['Pay for', 'the edge,', 'not the', 'noise.'];
    const heroAccent = heroWide ? 0 : 1;
    const heroSize = fitSize(heroLines, heroAccent, contentW, heroWide ? 88 : 60);
    const closer = user
        ? { label: 'Open the Optimizer', to: 'SameGameParlay' }
        : { label: 'Create a free account', to: 'Register' };

    return (
        <FLPage bottom={0} overlay={<PlanChangeSheet change={change} onClose={() => setChange(null)} onConfirm={confirmChange} />}>
            {/* Hero: the headline runs the full measure; the billing switch sits on the grid line under it. */}
            <View style={[styles.section, styles.hero, { paddingHorizontal: pad }, wide && { paddingTop: 72 }]}>
                <FLKicker index={1}>Plans</FLKicker>
                <FLHeadline lines={heroLines} accentLine={heroAccent} size={heroSize} style={{ marginTop: 22 }} />
                <View style={[styles.heroFoot, wide && styles.heroFootWide]}>
                    <Text style={[body(width >= 600 ? 19 : 17), styles.lede]}>
                        Every plan prices every bet against the whole market: fair odds, expected value and the best book for the number. Choose how much you search.
                    </Text>
                    <View style={[styles.billing, wide && { alignItems: 'flex-end' }]}>
                        <Text style={label(11)}>Billing</Text>
                        <View style={styles.billingRow}>
                            <BillingToggle value={billingInterval} onChange={setBillingInterval} />
                            {maxSave > 0 && (
                                <View style={[styles.saveTag, billingInterval === 'year' && styles.saveTagOn]}>
                                    <Text style={[styles.saveText, billingInterval === 'year' && { color: FL.onGreen }]}>Yearly saves up to {maxSave}%</Text>
                                </View>
                            )}
                        </View>
                        {isTester && (
                            <Pressable
                                onPress={() => setTestMode((t) => !t)}
                                style={[styles.testToggle, testMode && styles.testToggleOn]}
                                dataSet={{ flChip: testMode ? 'on' : '' }}
                                accessibilityRole="switch"
                                aria-checked={!!testMode}
                                accessibilityLabel="Stripe test mode for checkouts"
                            >
                                <Ionicons name={testMode ? 'flask' : 'flask-outline'} size={14} color={testMode ? FL.amber : FL.ink2} />
                                <Text style={[styles.testToggleText, testMode && { color: FL.amber }]}>{testMode ? 'Test mode on: use card 4242 4242 4242 4242' : 'Admin: use Stripe test mode'}</Text>
                            </Pressable>
                        )}
                    </View>
                </View>
            </View>

            {/* Status */}
            {(banner || (user && subscriber)) && (
                <View style={[styles.section, { paddingHorizontal: pad, marginTop: 32, gap: 12 }]}>
                    {banner && <StatusBanner banner={banner} onClose={() => setBanner(null)} />}
                    {user && subscriber && (
                        <Appear index={2}>
                            <SubscriptionSummary sub={sub} current={current} plans={plans} onManage={manage} busy={busyKey === 'portal'} />
                        </Appear>
                    )}
                </View>
            )}

            {/* Plans: glass cards, one per plan */}
            <View style={[styles.section, { paddingHorizontal: pad, marginTop: wide ? 56 : 40 }]}>
                <View style={styles.grid} accessibilityRole="list">
                    {plans.map((plan, i) => {
                        const action = planAction({ plan, interval: billingInterval, userData: userProfile || {}, signedIn: !!user, mode });
                        const featured = !!plan.highlight;
                        return (
                            <View
                                key={plan.key}
                                style={[styles.cellWrap, { width: cardWidth }]}
                                accessibilityRole="listitem"
                            >
                                <View style={[styles.cell, featured && styles.cellFeatured]} dataSet={{ flCard: '', planCell: featured ? 'featured' : 'plain' }}>
                                <View style={styles.cardBar} dataSet={{ flCardBar: '' }} aria-hidden />
                                <Reveal index={i} distance={28} style={{ flex: 1 }}>
                                    <PlanCard
                                        plan={plan}
                                        index={i}
                                        interval={billingInterval}
                                        action={action}
                                        isCurrent={current === plan.key && !!user}
                                        busy={busyKey === plan.key}
                                        onPress={() => onAction(plan, action)}
                                        tall={cols > 1}
                                        priceSize={cols === 4 ? 46 : 52}
                                    />
                                </Reveal>
                                </View>
                            </View>
                        );
                    })}
                </View>
                <CheckoutTerms color={FL.inkSoft} linkColor={FL.ink} navigateTo={navigateTo} />
            </View>

            {/* Bands */}
            <View style={{ marginTop: wide ? 96 : 64 }}>
                <FLMarquee items={BAND} size={wide ? 40 : 26} duration={40} />
                <FLMarquee items={BAND} size={wide ? 20 : 16} duration={30} reverse tone="green" style={{ paddingVertical: 10, borderTopWidth: 0 }} />
            </View>

            {/* Includes: every plan gets the same engine */}
            <View style={[styles.section, { paddingHorizontal: pad, marginTop: wide ? 112 : 72 }]}>
                <Reveal>
                    <FLKicker index={2}>In every plan</FLKicker>
                    <FLHeadline lines={['Same engine,', 'every plan']} size={wide ? 52 : 34} level={2} enter={false} style={{ marginTop: 18 }} />
                </Reveal>
                <View style={{ marginTop: 32 }} accessibilityRole="list">
                    <FLRule />
                    {INCLUDES.map(([t, b], i) => (
                        <Reveal key={t} index={i} distance={16}>
                            <View style={[styles.row, !wide && styles.rowStack]} dataSet={{ flRow: 'static' }} accessibilityRole="listitem">
                                <Text style={styles.rowNum} dataSet={{ flRowNum: '' }} aria-hidden>{String(i + 1).padStart(2, '0')}</Text>
                                <View style={[styles.rowBody, wide && { flexDirection: 'row', alignItems: 'baseline', gap: 24 }]} dataSet={{ flRowBody: '' }}>
                                    <Text style={[styles.rowTitle, wide && { width: '42%' }]}>{t}</Text>
                                    <Text style={[body(15), { flex: 1 }]} dataSet={{ flLit: '' }}>{b}</Text>
                                </View>
                                <View dataSet={{ flRowArrow: '' }} aria-hidden>
                                    <Ionicons name="arrow-forward" size={20} color={FL.greenInk} />
                                </View>
                            </View>
                        </Reveal>
                    ))}
                </View>
            </View>

            {/* FAQ: heading on the left, rows on the right (stacked on phones) */}
            <View style={[styles.section, { paddingHorizontal: pad, marginTop: wide ? 112 : 72 }, wide && styles.faqWide]}>
                <Reveal style={wide ? { flex: 1 } : undefined}>
                    <FLKicker index={3}>Questions</FLKicker>
                    <FLHeadline lines={['The fine', 'print']} size={wide ? 52 : 34} level={2} enter={false} style={{ marginTop: 18 }} />
                </Reveal>
                <View style={[wide ? { flex: 1.6 } : { marginTop: 32 }]}>
                    <FLRule />
                    {FAQ.map((f, i) => (
                        <FaqRow key={f.q} index={i} item={f} open={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? null : i)} />
                    ))}
                </View>
            </View>

            {/* Closer: a glass stage with a purple bloom */}
            <View style={[styles.closer, { paddingHorizontal: pad, marginTop: wide ? 120 : 80 }]}>
                <Reveal style={[styles.closerInner, { paddingHorizontal: wide ? 56 : 24 }]}>
                    <FLKicker index={4} tone="paper">Start</FLKicker>
                    <FLHeadline lines={['Find', 'your edge.']} accentLine={1} tone="paper" size={wide ? 84 : fitSize(['Find', 'your edge.'], 1, contentW - 48, 48)} level={2} enter={false} style={{ marginTop: 22 }} />
                    <Text style={[body(18), styles.closerBody]}>
                        {user ? 'Your plan works everywhere: search, the Optimizer and same-game parlays.' : 'Start free. No card needed, upgrade when the searches run out.'}
                    </Text>
                    <View style={styles.ctaRow}>
                        <FLButton label={closer.label} variant="green" onPress={() => navigateTo(closer.to)} size="lg" style={styles.cta} />
                        <FLButton label="Why BetOptimized" variant="paper" onPress={() => navigateTo('Why')} size="lg" style={styles.cta} />
                    </View>
                </Reveal>
            </View>
        </FLPage>
    );
}

/** Monthly / Yearly: a glass pill, the selected side on the accent fill; arrows move on web. */
function BillingToggle({ value, onChange }) {
    const options = [{ value: 'month', label: 'Monthly' }, { value: 'year', label: 'Yearly' }];
    const idx = options.findIndex((o) => o.value === value);
    const move = (d) => onChange(options[(idx + d + options.length) % options.length].value);
    return (
        <View
            style={styles.toggle}
            accessibilityRole="radiogroup"
            accessibilityLabel="Billing period"
            {...(IS_WEB ? { onKeyDown: (e) => { if (e.key === 'ArrowRight') move(1); if (e.key === 'ArrowLeft') move(-1); } } : {})}
        >
            {options.map((o, i) => {
                const on = o.value === value;
                return (
                    <Pressable
                        key={o.value}
                        onPress={() => !on && onChange(o.value)}
                        accessibilityRole="radio"
                        aria-checked={!!on}
                        accessibilityLabel={o.label}
                        dataSet={{ flChip: on ? 'on' : '' }}
                        style={[styles.toggleOpt, on && styles.toggleOptOn]}
                    >
                        <Text style={[styles.toggleText, on && { color: FL.onGreen }]}>{o.label}</Text>
                    </Pressable>
                );
            })}
        </View>
    );
}

function PlanCard({ plan, index, interval, action, isCurrent, busy, onPress, tall, priceSize }) {
    const free = plan.key === 'FREE';
    const amount = Number(plan.prices?.[interval]?.amount) || 0;
    const save = yearlySavings(plan);
    const featured = !!plan.highlight;
    const primary = action.kind === 'checkout' || action.kind === 'change' || action.kind === 'signin' || action.kind === 'manage-resume';
    const ink = FL.ink;
    const soft = FL.ink2;
    const faint = FL.muted;
    const variant = primary ? (featured ? 'green' : 'ink') : 'outline';
    const busyLabel = action.kind === 'checkout' ? 'Opening checkout' : action.kind === 'manage-resume' ? 'Resuming' : undefined;

    return (
        <View style={[styles.card, tall && { flex: 1 }]}>
            <View style={styles.cardHead}>
                <Text style={[label(12), { color: faint }]} aria-hidden>{String(index + 1).padStart(2, '0')}</Text>
                {featured && !isCurrent && <View style={[styles.tag, styles.tagAccent]}><Text style={[styles.tagText, { color: FL.onGreen }]}>Recommended</Text></View>}
                {isCurrent && <View style={[styles.tag, styles.tagCurrent]}><Text style={[styles.tagText, { color: FL.pos }]}>Your plan</Text></View>}
            </View>
            <Text style={[display(30, { width: 104, lineHeight: 1 }), { color: ink, marginTop: 18, letterSpacing: -0.4 }]} accessibilityRole="header" aria-level={3}>{plan.name}</Text>
            <Text style={[body(15), { color: soft, marginTop: 8 }, tall && { minHeight: 46 }]}>{plan.tagline}</Text>

            <View style={styles.priceRow} accessible accessibilityLabel={free ? 'Free' : `${formatPrice(amount)} per ${interval === 'year' ? 'year' : 'month'}`}>
                {free ? (
                    <Text style={[display(priceSize, { width: 100, lineHeight: 1 }), nums, { color: ink, letterSpacing: -priceSize * 0.03 }]}>$0</Text>
                ) : (
                    <AnimatedNumber value={amount} format={(v) => formatPrice(Math.round(v))} style={[display(priceSize, { width: 100, lineHeight: 1 }), nums, { color: ink, letterSpacing: -priceSize * 0.03 }]} />
                )}
                <Text style={[label(12), { color: faint, marginBottom: 6 }]}>{free ? 'forever' : interval === 'year' ? '/ year' : '/ month'}</Text>
            </View>
            <Text style={[styles.priceNote, nums, { color: faint }]}>
                {free ? 'No card needed.' : interval === 'year' ? `${formatPrice(Math.round(save.perMonth * 100) / 100)}/mo billed yearly · save ${formatPrice(save.saved)}` : `or ${formatPrice(plan.prices?.year?.amount || 0)}/yr, ${save.pct}% off`}
            </Text>

            <View style={styles.limit}>
                <Ionicons name="search" size={15} color={FL.greenInk} />
                <Text style={[styles.limitText, nums, { color: ink }]}>{limitLabel(plan)}</Text>
            </View>

            <View style={[styles.features, tall && { flex: 1 }]}>
                {displayFeatures(plan).map((f) => (
                    <View key={f} style={styles.featureRow}>
                        <View style={{ marginTop: 2 }}>
                            <Ionicons name="checkmark" size={16} color={FL.greenInk} />
                        </View>
                        <Text style={[body(14), { color: soft, flex: 1 }]}>{f}</Text>
                    </View>
                ))}
            </View>

            <FLButton
                label={action.label}
                onPress={onPress}
                disabled={action.disabled}
                busy={busy}
                busyLabel={busyLabel}
                variant={variant}
                arrow={primary}
                style={action.disabled ? { opacity: action.kind === 'current' ? 1 : 0.6 } : undefined}
                accessibilityLabel={`${action.label}${free ? '' : `, ${plan.name}`}`}
            />
        </View>
    );
}

/** A question row: brightens under the pointer or keyboard focus; the plus turns to a cross on the accent fill when open. */
function FaqRow({ index, item, open, onToggle }) {
    return (
        <View style={styles.faqItem}>
            <Pressable
                onPress={onToggle}
                style={styles.faqQ}
                dataSet={{ flRow: '' }}
                accessibilityRole="button"
                aria-expanded={!!open}
                accessibilityLabel={item.q}
            >
                <Text style={styles.rowNum} dataSet={{ flRowNum: '' }} aria-hidden>{String(index + 1).padStart(2, '0')}</Text>
                <Text style={styles.faqQText} dataSet={{ flRowBody: '' }}>{item.q}</Text>
                <View style={[styles.faqIcon, open && styles.faqIconOpen]} aria-hidden>
                    <Ionicons name="add" size={18} color={open ? FL.onGreen : FL.ink} style={{ transform: [{ rotate: open ? '45deg' : '0deg' }] }} />
                </View>
            </Pressable>
            <Collapse open={open}>
                <Text style={[body(15), styles.faqA]}>{item.a}</Text>
            </Collapse>
        </View>
    );
}

function StatusBanner({ banner, onClose }) {
    const tone = {
        success: { icon: 'checkmark', fill: FL.posWash, fg: FL.pos },
        warn: { icon: 'time', fill: FL.amberWash, fg: FL.amber },
        info: { icon: 'information', fill: FL.blueWash, fg: FL.blueInk },
        pending: { icon: 'sync', fill: FL.greenWash, fg: FL.greenInk },
    }[banner.tone] || { icon: 'information', fill: FL.blueWash, fg: FL.blueInk };
    return (
        <Appear from="scale">
            <View style={styles.banner} accessibilityLiveRegion="polite" role="status">
                <View style={[styles.bannerMark, { backgroundColor: tone.fill }]}>
                    {banner.tone === 'pending' ? <ActivityIndicator size="small" color={tone.fg} /> : <Ionicons name={tone.icon} size={22} color={tone.fg} />}
                </View>
                <View style={styles.bannerText}>
                    <Text style={styles.bannerTitle}>{banner.title}</Text>
                    {!!banner.body && <Text style={[body(14), { marginTop: 2 }]}>{banner.body}</Text>}
                </View>
                {banner.tone !== 'pending' && (
                    <Pressable onPress={onClose} style={styles.iconBtn} dataSet={{ flChip: '' }} accessibilityRole="button" accessibilityLabel="Dismiss">
                        <Ionicons name="close" size={18} color={FL.ink} />
                    </Pressable>
                )}
            </View>
        </Appear>
    );
}

function SubscriptionSummary({ sub, current, plans, onManage, busy }) {
    const plan = plans.find((p) => p.key === current);
    const end = sub.currentPeriodEnd ? new Date(sub.currentPeriodEnd) : null;
    const endText = end && !Number.isNaN(end.getTime()) ? end.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : null;
    const status = String(sub.subscriptionStatus || '');
    const line = status === 'cancel_pending' ? `Ends ${endText || 'at the end of this period'}. You can resume before then.`
        : status === 'past_due' ? 'Your last payment failed. Update your card to keep your plan.'
            : endText ? `Renews ${endText}${sub.interval === 'year' ? ' (yearly)' : ''}.` : 'Active.';
    return (
        <View style={[styles.summary, status === 'past_due' && { borderColor: FL.amber }]}>
            <View style={{ flex: 1, minWidth: 220 }}>
                <Text style={label(11)}>{sub.livemode === false ? 'Test subscription' : 'Your subscription'}</Text>
                <Text style={[display(28, { width: 104, lineHeight: 1 }), { marginTop: 8, letterSpacing: -0.4 }]}>{plan?.name || current}</Text>
                <Text style={[body(15), { marginTop: 6 }, status === 'past_due' && { color: FL.amber }]}>{line}</Text>
            </View>
            <FLButton
                label={status === 'past_due' ? 'Update payment' : 'Manage billing'}
                variant="outline"
                onPress={onManage}
                busy={busy}
                accessibilityLabel="Manage billing, invoices and payment method"
                style={{ minWidth: 220 }}
            />
        </View>
    );
}

function PlanChangeSheet({ change, onClose, onConfirm }) {
    const open = !!change;
    const p = change?.preview;
    return (
        <Modal visible={open} transparent animationType="fade" onRequestClose={onClose}>
            <View style={styles.sheetBackdrop}>
                <Appear from="scale" style={styles.sheet}>
                    <Text style={[display(24, { width: 104, lineHeight: 1.05 }), { letterSpacing: -0.3 }]} accessibilityRole="header">Switch to {change?.plan?.name}{change?.interval === 'year' ? ' yearly' : ' monthly'}</Text>
                    {change?.loading && <View style={{ paddingVertical: 24 }}><ActivityIndicator color={FL.ink} /></View>}
                    {!change?.loading && p && (
                        <>
                            <Text style={body(15)}>
                                {p.amountDue > 0
                                    ? `You'll be charged ${formatPrice(p.amountDue.toFixed(2) * 1)} today for the rest of this billing period, then the new price on renewal.`
                                    : p.total < 0
                                        ? `Nothing is charged today. ${formatPrice(Math.abs(p.total).toFixed(2) * 1)} of unused time is credited to your next invoice.`
                                        : 'Nothing is charged today. The new price starts on your next renewal.'}
                            </Text>
                            <Text style={[body(13), { color: FL.muted }]}>Your new limits apply right away.</Text>
                        </>
                    )}
                    {!!change?.error && <Text style={[body(15), { color: FL.red }]}>{change.error}</Text>}
                    <View style={styles.sheetActions}>
                        <FLButton label="Not now" variant="outline" arrow={false} onPress={onClose} style={{ flex: 1 }} />
                        <FLButton label="Confirm switch" variant="green" onPress={onConfirm} disabled={!p} busy={!!change?.applying} style={{ flex: 1 }} />
                    </View>
                </Appear>
            </View>
        </Modal>
    );
}

const WEB = Platform.OS === 'web';
const ACCENT_FILL = WEB ? { backgroundImage: ACCENT_GRADIENT } : null;

const styles = StyleSheet.create({
    section: { alignSelf: 'center', width: '100%', maxWidth: 1480 },
    hero: { paddingTop: 48 },
    heroFoot: { marginTop: 36, gap: 28 },
    heroFootWide: { flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between', gap: 56 },
    lede: { maxWidth: 640, color: FL.ink2 },
    billing: { gap: 10 },
    billingRow: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 12 },
    toggle: { ...glass({ radius: 999 }), flexDirection: 'row', alignSelf: 'flex-start', padding: 4, gap: 4 },
    toggleOpt: { minHeight: 44, paddingHorizontal: 22, borderRadius: 999, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: 'transparent' },
    toggleOptOn: { backgroundColor: FL.green, ...ACCENT_FILL },
    toggleText: { fontFamily: FONT, fontSize: 13, fontWeight: '700', letterSpacing: 1.1, textTransform: 'uppercase', color: FL.ink2 },
    saveTag: { paddingHorizontal: 12, paddingVertical: 7, borderRadius: 999, borderWidth: 1, borderColor: FL.hair2 },
    saveTagOn: { backgroundColor: FL.green, borderColor: 'transparent', ...ACCENT_FILL },
    saveText: { ...label(11), color: FL.ink2 },
    testToggle: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 14, minHeight: 40, borderRadius: 999, borderWidth: 1, borderColor: FL.hair2, alignSelf: 'flex-start' },
    testToggleOn: { borderColor: FL.amber, backgroundColor: FL.amberWash },
    testToggleText: { fontFamily: FONT, fontSize: 12, fontWeight: '600', color: FL.ink2 },

    grid: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'stretch', marginHorizontal: -8 },
    cellWrap: { padding: 8 },
    cell: { ...glass({ radius: 22 }), flex: 1, overflow: 'hidden' },
    cellFeatured: {
        backgroundColor: FL.stage,
        borderColor: 'rgba(162,89,255,0.45)',
        ...(WEB ? { backgroundImage: 'radial-gradient(ellipse 90% 60% at 50% 0%, rgba(124,58,237,0.22) 0%, transparent 70%)', boxShadow: '0 0 60px rgba(124,58,237,0.14)' } : {}),
    },
    cardBar: { position: 'absolute', top: 0, left: 0, right: 0, height: 2, backgroundColor: FL.green, ...ACCENT_FILL, zIndex: 1 },
    card: { paddingTop: 26, paddingBottom: 24, paddingHorizontal: 24 },
    cardHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8, minHeight: 26 },
    tag: { paddingHorizontal: 10, paddingVertical: 5, borderRadius: 999 },
    tagAccent: { backgroundColor: FL.green, ...ACCENT_FILL },
    tagCurrent: { backgroundColor: FL.posWash, borderWidth: 1, borderColor: 'rgba(74,222,128,0.4)' },
    tagText: { fontFamily: FONT, fontSize: 10.5, fontWeight: '800', letterSpacing: 1.1, textTransform: 'uppercase' },
    priceRow: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'flex-end', gap: 8, marginTop: 22 },
    priceNote: { fontFamily: FONT, fontSize: 13, lineHeight: 18, marginTop: 8, minHeight: 18 },
    limit: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 18, paddingVertical: 10, paddingHorizontal: 12, borderRadius: 12, borderWidth: 1, borderColor: FL.hair, backgroundColor: FL.paper2, alignSelf: 'flex-start' },
    limitText: { fontFamily: FONT, fontSize: 14, fontWeight: '700', letterSpacing: -0.1 },
    features: { gap: 10, marginTop: 18, marginBottom: 24 },
    featureRow: { flexDirection: 'row', gap: 10, alignItems: 'flex-start' },

    row: { flexDirection: 'row', alignItems: 'center', gap: 20, paddingVertical: 24, paddingHorizontal: 12, borderBottomWidth: 1, borderBottomColor: FL.hair },
    rowStack: { alignItems: 'flex-start' },
    rowNum: { ...label(12), color: FL.muted, width: 34, letterSpacing: 1.6 },
    rowBody: { flex: 1, gap: 6 },
    rowTitle: { fontFamily: FONT, fontSize: 20, fontWeight: '700', color: FL.ink, letterSpacing: -0.3 },

    faqWide: { flexDirection: 'row', gap: 56, alignItems: 'flex-start' },
    faqItem: { borderBottomWidth: 1, borderBottomColor: FL.hair },
    faqQ: { flexDirection: 'row', alignItems: 'center', gap: 16, paddingVertical: 20, paddingHorizontal: 12, minHeight: 64 },
    faqQText: { flex: 1, fontFamily: FONT, fontSize: 18, fontWeight: '700', color: FL.ink, letterSpacing: -0.2 },
    faqIcon: { width: 34, height: 34, borderRadius: 999, borderWidth: 1, borderColor: FL.hair2, backgroundColor: FL.card, alignItems: 'center', justifyContent: 'center' },
    faqIconOpen: { backgroundColor: FL.green, borderColor: 'transparent', ...ACCENT_FILL },
    faqA: { paddingBottom: 24, paddingRight: 28, paddingLeft: 62, maxWidth: 760 },

    closer: { paddingBottom: 96 },
    closerInner: {
        ...glass({ strong: true, radius: 28 }),
        alignSelf: 'center', width: '100%', maxWidth: 1480, paddingTop: 64, paddingBottom: 72, overflow: 'hidden',
        ...(WEB ? { backgroundImage: 'radial-gradient(ellipse 70% 90% at 100% 100%, rgba(37,99,235,0.22) 0%, transparent 65%), radial-gradient(ellipse 60% 80% at 0% 0%, rgba(124,58,237,0.24) 0%, transparent 65%)' } : {}),
    },
    closerBody: { color: FL.stageSoft, marginTop: 24, maxWidth: 560 },
    ctaRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginTop: 32 },
    cta: { minWidth: 220 },

    banner: { ...glass({ radius: 18 }), flexDirection: 'row', alignItems: 'center', paddingLeft: 14 },
    bannerMark: { width: 40, height: 40, borderRadius: 999, alignItems: 'center', justifyContent: 'center' },
    bannerText: { flex: 1, paddingVertical: 14, paddingHorizontal: 14 },
    bannerTitle: { fontFamily: FONT, fontSize: 16, fontWeight: '800', color: FL.ink },
    iconBtn: { width: 44, height: 44, margin: 6, borderRadius: 999, alignSelf: 'center', alignItems: 'center', justifyContent: 'center' },
    summary: { ...glass({ radius: 20 }), flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 20, padding: 24 },

    sheetBackdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', alignItems: 'center', justifyContent: 'center', padding: 16, ...(WEB ? { backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' } : {}) },
    sheet: {
        ...glass({ strong: true, radius: 24 }),
        width: '100%', maxWidth: 460, backgroundColor: FL.paper, borderColor: FL.hair2, padding: 24, gap: 12,
        ...(WEB ? { backgroundImage: 'linear-gradient(rgba(255,255,255,0.07), rgba(255,255,255,0.07))', boxShadow: '0 30px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(162,89,255,0.18)' } : {}),
    },
    sheetActions: { flexDirection: 'row', gap: 10, marginTop: 10 },
});
